
import React from 'react';
import { Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ActivityAutocomplete from './ActivityAutocomplete';
import CityAutocomplete from './CityAutocomplete';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Slider } from '@/components/ui/slider';
import { ActivityCategory } from '@/types';

interface SearchFiltersProps {
  query: string;
  setQuery: (query: string) => void;
  location: string;
  setLocation: (location: string) => void;
  category: string;
  setCategory: (category: string) => void;
  minPrice: number;
  setMinPrice: (price: number) => void;
  maxPrice: number;
  setMaxPrice: (price: number) => void;
  onSearch: () => void;
}

const SearchFilters = ({
  query,
  setQuery,
  location,
  setLocation,
  category,
  setCategory,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  onSearch,
}: SearchFiltersProps) => {
  const categoryOptions: { value: ActivityCategory; label: string }[] = [
    { value: 'bowling', label: 'Bowling' },
    { value: 'karting', label: 'Karting' },
    { value: 'escape-game', label: 'Escape Game' },
    { value: 'laser-game', label: 'Laser Game' },
    { value: 'padel', label: 'Padel' },
    { value: 'foot-indoor', label: 'Foot Indoor' },
    { value: 'trampoline-park', label: 'Trampoline Park' },
    { value: 'billard', label: 'Billard' },
    { value: 'paintball-indoor', label: 'Paintball Indoor' },
    { value: 'vr-arcade', label: 'VR Arcade' },
  ];

  const handlePriceChange = (value: number[]) => {
    setMinPrice(value[0]);
    setMaxPrice(value[1]);
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
      <div className="hidden md:flex flex-col md:flex-row gap-4">
        <ActivityAutocomplete
          value={query}
          onChange={setQuery}
          placeholder="Rechercher une activité..."
          className="flex-1"
        />

        <CityAutocomplete
          value={location}
          onChange={setLocation}
          placeholder="Où ? (ville, arrondissement...)"
        />

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full md:w-52 border-0 bg-gray-50">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {categoryOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={onSearch}
          className="bg-brand-primary hover:bg-brand-primary/90 shrink-0"
        >
          Rechercher
        </Button>
      </div>

      {/* Mobile search bar with filters sheet */}
      <div className="flex gap-2 md:hidden">
        <ActivityAutocomplete
          value={query}
          onChange={setQuery}
          placeholder="Rechercher..."
          className="flex-1"
        />

        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon">
              <Filter size={18} />
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[75vh] max-h-[600px] sm:h-[85vh]">
            <SheetHeader>
              <SheetTitle>Filtres</SheetTitle>
              <SheetDescription>
                Affinez votre recherche avec nos filtres
              </SheetDescription>
            </SheetHeader>
            <div className="py-4">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="location">
                  <AccordionTrigger>Lieu</AccordionTrigger>
                  <AccordionContent>
                    <CityAutocomplete
                      value={location}
                      onChange={setLocation}
                      placeholder="Où ? (ville, arrondissement...)"
                    />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="category">
                  <AccordionTrigger>Catégorie</AccordionTrigger>
                  <AccordionContent>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger className="w-full border bg-white">
                        <SelectValue placeholder="Catégorie" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les catégories</SelectItem>
                        {categoryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="price">
                  <AccordionTrigger>Prix</AccordionTrigger>
                  <AccordionContent>
                    <div className="px-2">
                      <Slider
                        defaultValue={[minPrice, maxPrice]}
                        max={100}
                        step={5}
                        onValueChange={handlePriceChange}
                        className="my-6"
                      />
                      <div className="flex justify-between">
                        <span>{minPrice}€</span>
                        <span>{maxPrice}€</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            <div className="flex gap-4 mt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setQuery('');
                  setLocation('');
                  setCategory('');
                  setMinPrice(0);
                  setMaxPrice(100);
                }}
              >
                Réinitialiser
              </Button>
              <SheetClose asChild>
                <Button className="flex-1 bg-brand-primary" onClick={onSearch}>
                  Appliquer les filtres
                </Button>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>

        <Button
          onClick={onSearch}
          size="icon"
          className="bg-brand-primary hover:bg-brand-primary/90"
        >
          <Search size={18} />
        </Button>
      </div>
    </div>
  );
};

export default SearchFilters;
